import styled from 'styled-components';

import { Trans } from 'services/i18n';
import { Avatar, AVATAR_SIZES } from 'ui/avatar';
import { colors } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { localizeDateTime, localizeRelativeTimeSince, DateFormat, TimeFormat } from 'utils/l10n';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';

import { Cell } from './table/Cell';
import { TABLE_COLUMNS } from './constants';
import { useTranslation } from '../../../hooks';
import { ListReference, CreatorAndModifierById, ListReferenceUser } from '../../../types';

type ListReferenceLastModifiedCellProps = {
  listReference: ListReference;
  rowIndex: number;
  creatorsAndModifiersById: CreatorAndModifierById;
};

const LastModifiedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ModifiedDateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
`;

const ModifiedAgoWrapper = styled.div`
  ${({ theme }) => theme.styles.captions.caption1}
  color: ${colors.textSecondary};
`;

export const testIds = {
  cell: 'segmentation-lrlmc-cell',
};

const DateTooltipContent = ({ date, name }: { date: string | number; name: string }) => {
  const { t } = useTranslation();
  return (
    <>
      {localizeDateTime(new Date(date), {
        dateStyle: DateFormat.Short,
        showTime: true,
        timeStyle: TimeFormat.TwelveHour,
      })}
      <br />
      {t('insights.listUsage.createdBy', { name })}
    </>
  );
};

const AvatarTooltipContent = ({ email, name }: { email: string; name?: string }) => (
  <div>
    {name && <div>{name}</div>}
    <div>{email}</div>
  </div>
);

const getUpdatedBy = (dependent: ListReference) => {
  if (dependent.__typename === GRAPHQL_TYPENAMES.LIST) {
    return dependent.creatorUserId;
  } else if (dependent.__typename === GRAPHQL_TYPENAMES.CAMPAIGN) {
    return dependent.updatedBy || dependent.createdBy;
  } else {
    return dependent.updatedByUserId || dependent.creatorUserId;
  }
};

const getUserInfo = (userInfo: ListReferenceUser | null, defaultId: string): ListReferenceUser => {
  return {
    id: userInfo ? userInfo.id : defaultId,
    fullName: userInfo ? userInfo.fullName : '',
    ...(userInfo && { avatarUrl: userInfo.avatarUrl }),
  };
};

export const ListReferenceLastModifiedCell = ({
  listReference: dependent,
  rowIndex,
  creatorsAndModifiersById,
}: ListReferenceLastModifiedCellProps) => {
  const updatedByUserId = getUpdatedBy(dependent);
  const { t } = useTranslation();
  const {
    id: avatarEmail,
    fullName: avatarName,
    avatarUrl,
  } = getUserInfo(creatorsAndModifiersById[updatedByUserId], updatedByUserId);

  const modifiedDate = localizeDateTime(new Date(dependent.updatedAt || dependent.createdAt), {
    dateStyle: DateFormat.Short,
    relative: true,
    showTime: true,
    timeStyle: TimeFormat.TwelveHour,
  });
  const modifiedAgo = localizeRelativeTimeSince(
    new Date(dependent.updatedAt || dependent.createdAt),
  );

  return (
    <Cell
      columnId={TABLE_COLUMNS.LAST_MODIFIED}
      data-test={testIds.cell}
      {...(rowIndex % 2 === 0 && { className: 'shadedRow' })}
    >
      <LastModifiedWrapper>
        <Tooltip
          isEnabled={!!avatarName || !!avatarEmail}
          tooltipContent={<AvatarTooltipContent email={avatarEmail} name={avatarName} />}
        >
          <Avatar size={AVATAR_SIZES.MEDIUM} firstName={avatarName} imgSrc={avatarUrl} />
        </Tooltip>
        <ModifiedDateWrapper>
          {modifiedDate}
          <ModifiedAgoWrapper>
            <Trans
              t={t}
              i18nKey="insights.listUsage.createdAt"
              values={{ modifiedAgo }}
              components={{
                Tooltip: (
                  <Tooltip
                    isUnderlined
                    tooltipContent={
                      <DateTooltipContent date={dependent.updatedAt} name={updatedByUserId} />
                    }
                  >
                    <>{{ modifiedAgo }}</>
                  </Tooltip>
                ),
              }}
            />
          </ModifiedAgoWrapper>
        </ModifiedDateWrapper>
      </LastModifiedWrapper>
    </Cell>
  );
};
